import type { TFunction } from "i18next";

class StringHelpers {
  /**
   * @author Carden
   * @description A common method to remove Unicode characters introduce
   * by implicit component formatters e.g. input masks
   */
  static cleanInvisibleChars(str: string) {
    return str.replace(/(⁩|⁦)/g, "");
  }

  static capitalize(str: string) {
    return str
      .replace(/_/g, " ")
      .toLowerCase()
      .split(" ")
      .filter(Boolean)
      .map((word) => word.replace(word[0], word[0].toUpperCase()))
      .join(" ");
  }

  static cleanWhitespace(str: string) {
    return str.split(" ").filter(Boolean).join(" ");
  }

  /**
   * @description Removes diacritics from a string, e.g. "á" -> "a"
   */
  static removeDiacritics(str: string) {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  static splitDiacriticStrings(originalText: string, searchText: string) {
    const normalizedText = StringHelpers.removeDiacritics(originalText).toLowerCase();
    const regex = new RegExp(
      `(${StringHelpers.removeDiacritics(searchText).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    const normalizedParts = normalizedText.split(regex);

    const parts: string[] = [];

    let partStart = 0;
    normalizedParts.forEach((part) => {
      const partLength = part.length;
      const originalPart = `${originalText}`.substring(partStart, partStart + partLength);
      parts.push(originalPart);
      partStart = partStart + partLength;
    });

    return parts;
  }

  static slugify(text: string) {
    return text
      .toString()
      .toLowerCase()
      .replace(/\s+/g, "-") // Replace spaces with -
      .replace(/[^\w-]+/g, "") // Remove all non-word chars
      .replace(/--+/g, "-") // Replace multiple - with single -
      .replace(/^-+/, "") // Trim - from start of text
      .replace(/-+$/, ""); // Trim - from end of text
  }

  /**
   * This checks if a string starts with a Unicode letter.
   * It covers a wide range of Unicode letters across various scripts.
   * Because we use es5 we can't use \p{L} in regex, so we use code point ranges instead.
   */
  static isFirstCharLetter(s: string): boolean {
    const code = s.codePointAt(0);
    if (code === undefined) return false;

    // Unicode "Letter" ranges (categories: Lu, Ll, Lt, Lm, Lo)
    return (
      (code >= 65 && code <= 90) || // Latin uppercase A-Z
      (code >= 97 && code <= 122) || // Latin lowercase a-z
      (code >= 170 && code <= 170) || // ª
      (code >= 181 && code <= 181) || // µ
      (code >= 186 && code <= 186) || // º
      (code >= 192 && code <= 214) || // À-Ö
      (code >= 216 && code <= 246) || // Ø-ö
      (code >= 248 && code <= 705) || // ø-ˇ
      (code >= 710 && code <= 721) || // ˆ-ˑ
      (code >= 736 && code <= 740) || // Ͱ-ʹ
      (code >= 7424 && code <= 8191) || // Various scripts: Latin extended, Greek, Cyrillic
      (code >= 8304 && code <= 8591) || // Superscripts and letters
      (code >= 11264 && code <= 12271) || // Cyrillic, Georgian, Armenian
      (code >= 12288 && code <= 55203) || // Broad range: CJK, Hangul, etc.
      (code >= 63744 && code <= 64255) || // Hangul Compatibility Jamo
      (code >= 65072 && code <= 65103) || // Latin extended additional
      (code >= 65313 && code <= 65338) || // Full width A-Z
      (code >= 65345 && code <= 65370) // Full width a-z
    );
  }

  /**
   * Checks if a string starts with a digit (0-9).
   */
  static isFirstCharDigit(s: string) {
    return /^\d/.test(s);
  }

  /**
   * Normalizes a string for search comparison by trimming, lowercasing, and removing diacritics.
   * This ensures that searches are case-insensitive and diacritic-insensitive, providing a more user-friendly search experience.
   */
  static normalize(str: string) {
    return StringHelpers.removeDiacritics(str.trim().toLowerCase());
  }

  /**
   * Truncates a list of names to a maximum number and appends a localized "others" count if necessary.
   * @param names - The list of names to truncate.
   * @param t - The translation function from i18next.
   * @param max - The maximum number of names to show before truncating.
   * @returns An array of names with a possible "others" count appended.
   */
  static truncatedNames(names: string[], t: TFunction, max = 2): string[] {
    const shown = names.slice(0, max);
    const rest = names.length - shown.length;
    return rest > 0 ? [...shown, t("global.others_count", { count: rest })] : shown;
  }
}
export default StringHelpers;
